'use client';

import { GoogleAds, useScreenDetect } from '@sgcmpteltd/uikit-next';
import type {
  OnSlotRenderEnded,
} from '@sgcmpteltd/uikit-next/components/google-ads';
import { Pill, Select } from '@sgcmpteltd/uikit-react';
import {
  MENU_BORDER_RADIUS_EXTRA_LARGE,
  MENU_LAYER_POSITION_FIXED,
} from '@sgcmpteltd/uikit-react/components/select';
import cx from 'clsx';
import Cookies from 'js-cookie';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { ACTION_STATUS_SUCCESS } from '@/actions';
import { getPromotionsData } from '@/actions/new-car/promotions';
import type {
  CamelCaseRoadshowItem,
} from '@/apis/server/misc/v1/road-show/types';
import type {
  CamelCaseAdAlert,
  CamelCaseCarAlert,
  CamelCaseResponseData as CamelCaseMyAlert,
} from '@/apis/server/new-car/v1/my-alert';
import { isCamelCaseAdAlert } from '@/apis/server/new-car/v1/my-alert/utils';
import type {
  CamelCasePromotion,
  CamelCasePromotionList,
} from '@/apis/server/new-car/v1/promotions';
import {
  PROMOTION_TYPE_TACTICAL_ADS,
} from '@/apis/server/new-car/v1/promotions/constants';
import {
  PromotionDetailsModal,
} from '@/app/_components/page_content/new-car-promo/promotion-details-modal';
import {
  SearchFilterSection,
} from '@/app/_components/page_content/search-filter-section';
import {
  AlertModal,
} from '@/app/(routes)/listing/_components/page-content/alert-modal';
import type { User } from '@/app/types';
import { NewCarBreadcrumb as Breadcrumb } from '@/components/breadcrumb';
import {
  COOKIE_KEY_MAIL,
  COOKIE_KEY_SESSION_ID,
  COOKIE_KEY_USER_ID,
} from '@/configs/cookies';
import {
  promoListingBanner1,
  promoListingBanner2,
} from '@/configs/google/ads/mobile';
import {
  AUTHORISED_DISTRIBUTORS,
  PARALLEL_IMPORTERS,
} from '@/constants/global';
import { AppContext } from '@/context/app';
import { createTimestampUid } from '@/utils/id';
import {
  joinSgcmMedia,
  joinSgcmMsSite,
  joinSgcmSite,
} from '@/utils/url/join/public';

import { Pagination } from '../../../../../../components/pagination';
import { breadcrumbList } from './configs';
import {
  ALL_NEW_PROMOTIONS,
  ALL_STATUS,
  ELECTRIC_VEHICLES,
  NO_SEARCH_RESULT_ALT,
  NO_SEARCH_RESULT_IMAGE,
  NO_SEARCH_RESULT_IMAGE_HEIGHT,
  NO_SEARCH_RESULT_IMAGE_WIDTH,
  PAGE_TITLE_ALL_NEW_PROMOTIONS_LIST,
  PILL_ACTIVE_BACKGROUND_COLOR_RED_1,
  PILL_ACTIVE_TEXT_WEIGHT_SEMIBOLD,
  PILL_BACKGROUND_COLOR_GRAY_1,
} from './constants';
import { NewCarShowcase } from './new-car-showcase';
import { PromoByCarMake } from './promo-by-car-make';
import { PromoItem } from './promo-item';
import styles from './styles.module.scss';
import type { Component, DealerType } from './types';

const isBrowser = () => typeof window !== 'undefined';

export const PageContent: Component = ({
  brandList,
  allPromotion,
  dealerList,
  myAlerts = null,
  roadShowList,
}) => {
  const [
    promotionList,
    setPromotionList,
  ] = useState<CamelCasePromotionList | null >(null);

  const rawUrl = isBrowser() ? window.location.href : '';
  const utf8Bytes = new TextEncoder().encode(rawUrl);
  const currentUrlInBase64 = Buffer.from(utf8Bytes).toString('base64');
  const [selectedTab, setSelectedTab] = useState<DealerType>(AUTHORISED_DISTRIBUTORS); // @suppress longLineCheck
  const [selectBrandValue, setSelectBrandValue] = useState<string>('');
  const [selectStatusValue, setSelectStatusValue] = useState<string>('');

  const router = useRouter();
  const searchParams = useSearchParams();

  const [totalNumberOfPromo, setTotalNumberOfPromo] = useState<
  number | null>(null);

  const [currentPage, setCurrentPage] = useState<number | null>(1);
  const [lastPage, setLastPage] = useState<number | null>(null);

  const appContextData = useContext(AppContext);
  const { isDesktop, isMobile } = useScreenDetect();
  const isDesktopMode = isDesktop();
  const isMobileMode = isMobile();

  const handlePageChange = async (page: number) => {
    const params = new URLSearchParams(searchParams.toString());

    params.delete('pid');
    params.set('page', String(page));

    router.push(`?${params.toString()}`);
  };

  const handleFilterClick = () => {
    appContextData?.setIsFilterModalOpen(true);
  };

  const handlePriceListTypeChange = (
    type: DealerType,
  ) => () => {
    setSelectedTab(type);
    const params = new URLSearchParams(searchParams.toString());

    params.delete('page');
    params.delete('pid');
    params.set('active_tab', String(type));

    router.push(`?${params.toString()}`);
  };

  const handleSelectBrandOnClick = (value: string | number | boolean) => {
    setSelectBrandValue(String(value));
    const params = new URLSearchParams(searchParams.toString());

    params.delete('page');
    params.delete('pid');
    params.set('brand', String(value));

    router.push(`?${params.toString()}`);
  };

  const handleSelectStatusOnClick = (value: string | number | boolean) => {
    setSelectStatusValue(String(value));
    const params = new URLSearchParams(searchParams.toString());

    params.delete('page');
    params.delete('pid');
    params.set('status', String(value));

    router.push(`?${params.toString()}`);
  };

  const getSelectedTabLabel = (tab: DealerType) => {
    switch (tab) {
      case AUTHORISED_DISTRIBUTORS:
        return 'Authorised Distributors';
      case PARALLEL_IMPORTERS:
        return 'Parallel Importers';
      case ELECTRIC_VEHICLES:
        return 'Electric Vehicle';
      case ALL_NEW_PROMOTIONS:
        return '';
      default:
        return '';
    }
  };

  const [
    promotionDetailsModalKey,
    setPromotionDetailsModalKey,
  ] = useState(createTimestampUid);

  const [
    isPromotionDetailsModalOpen,
    setIsPromotionDetailsModalOpen,
  ] = useState(false);

  const [
    activePromotionListPromoId,
    setActivePromotionListPromoId,
  ] = useState<number | null>(null);

  const activePromotionDetailsItem = allPromotion
    ? allPromotion.find(
      promotion => promotion.id === activePromotionListPromoId,
    ) as CamelCasePromotion
    : null;

  useEffect(() => {
    const brand = searchParams.get('brand');
    const dealerType = searchParams.get('active_tab');

    setSelectBrandValue(brand || '');
    setSelectedTab(dealerType as DealerType || AUTHORISED_DISTRIBUTORS);
  }, [searchParams]);

  const isExpired = (item: CamelCasePromotion) => {
    const { endsOn } = item;

    if (endsOn?.includes('Promotion ends on:')) {
      const dateStr = endsOn.replace('Promotion ends on:', '').trim();
      const [day, monthStr, year] = dateStr.split('-');

      const monthMap: Record<string, number> = {
        Jan: 0,
        Feb: 1,
        Mar: 2,
        Apr: 3,
        May: 4,
        Jun: 5,
        Jul: 6,
        Aug: 7,
        Sep: 8,
        Oct: 9,
        Nov: 10,
        Dec: 11,
      };

      const parsedDate = new Date(
        Number(year),
        monthMap[monthStr],
        Number(day),
      );

      const oneDayInMs = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      const parsedDatePlusOneDay = new Date(parsedDate.getTime() + oneDayInMs);
      return parsedDatePlusOneDay < new Date();
    }

    return false;
  };

  useEffect(() => {
    const fetchData = async () => {
      const formData = new FormData();

      const make = searchParams.get('brand');
      const dealerType = searchParams.get('active_tab');
      const pageParam = searchParams.get('page');
      const currentPageFromUrl = pageParam ? parseInt(pageParam, 10) : 1;
      const status = searchParams.get('status');

      let filteredData = allPromotion || [];

      if (dealerType === ELECTRIC_VEHICLES) {
        formData.set('fuelType', 'electric');
        const promotionsActionResult = await getPromotionsData(formData);

        if (promotionsActionResult.status !== ACTION_STATUS_SUCCESS) {
          setPromotionList(null);
        } else {
          const { data } = promotionsActionResult.data;

          filteredData = data;
        }
      }

      if (make) {
        filteredData = filteredData.filter(item => item.make === make);
      }

      if (dealerType === PARALLEL_IMPORTERS) {
        filteredData = filteredData.filter(
          item => item.dealer.type === PARALLEL_IMPORTERS,
        );
      }

      if (dealerType === AUTHORISED_DISTRIBUTORS
        || selectedTab === AUTHORISED_DISTRIBUTORS) {
        filteredData = filteredData.filter(
          item => item.dealer.type === AUTHORISED_DISTRIBUTORS,
        );
      }

      if (status === 'e') {
        filteredData = filteredData.filter(isExpired);
      } else if (status === 'a') {
        filteredData = filteredData.filter(item => !isExpired(item));
      }

      setTotalNumberOfPromo(filteredData.length);

      const sortedData = [...filteredData].sort((a, b) => {
        const aIsPriority = a.dealer.type === 'ad'
          && a.promoType === PROMOTION_TYPE_TACTICAL_ADS;
        const bIsPriority = b.dealer.type === 'ad'
          && b.promoType === PROMOTION_TYPE_TACTICAL_ADS;

        if (aIsPriority === bIsPriority) return 0;
        return aIsPriority ? -1 : 1;
      });

      // Restructure sortedData by separating expired and non-expired promotions
      const nonExpiredPromotions = sortedData.filter(item => !isExpired(item));
      const expiredPromotions = sortedData.filter(item => isExpired(item));

      // Concatenate non-expired promotions first, followed by expired ones
      const restructuredData = [...nonExpiredPromotions, ...expiredPromotions];

      const itemsPerPage = 15;
      const totalItems = restructuredData.length;
      const calculatedLastPage = Math.ceil(totalItems / itemsPerPage);

      setLastPage(calculatedLastPage);
      setCurrentPage(currentPageFromUrl);

      const startIndex = (currentPageFromUrl - 1) * itemsPerPage;
      const endIndex = currentPageFromUrl * itemsPerPage;

      const paginatedData = restructuredData.slice(startIndex, endIndex);

      setPromotionList(paginatedData);
    };

    fetchData();
  }, [searchParams, selectedTab, allPromotion]);

  const [
    isAlertModalOpen,
    setIsAlertModalOpen,
  ] = useState(false);

  const adAlertInit = myAlerts?.adAlerts
    .find(alert => alert.make === selectBrandValue) || null;

  const filteredDealers = dealerList.filter(
    item => item.brand === selectBrandValue,
  );

  const [
    myAlert,
    setMyAlert,
  ] = useState<CamelCaseMyAlert | null>(myAlerts);

  const [adAlert, setAdAlert] = useState<CamelCaseAdAlert | null>(adAlertInit);

  const user: User = {
    email: Cookies.get(COOKIE_KEY_MAIL) || '',
    userId: Number(Cookies.get(COOKIE_KEY_USER_ID)) || 0,
    sessionId: Cookies.get(COOKIE_KEY_SESSION_ID) || '',
  };

  const handleAlertClick = () => {
    if (user.sessionId) {
      setIsAlertModalOpen(true);
    } else {
      window.open(
        joinSgcmMsSite(`/member/login?referrer=${currentUrlInBase64}`),
        '_self',
      );
    }
  };

  const handleAdAlertModalSubmit = (
    alert: CamelCaseAdAlert | CamelCaseCarAlert,
  ) => {
    if (isCamelCaseAdAlert(alert) && myAlert) {
      // remove
      if (!alert.adCarLaunches && !alert.adPromo) {
        const newAdAlert = myAlert?.adAlerts
          .filter(ad => ad.make !== alert.make);

        if (newAdAlert) {
          setMyAlert({ ...myAlert, adAlerts: newAdAlert });
          setAdAlert(alert);
        }
      } else { // add
        setMyAlert({ ...myAlert, adAlerts: [...myAlert.adAlerts, alert] });
        setAdAlert(alert);
      }
    }
  };

  const handleAlertModalSubmit = (
    alerts: CamelCaseAdAlert | CamelCaseCarAlert,
  ) => {
    handleAdAlertModalSubmit?.(alerts);
  };

  const adAlertModalContent = {
    adInfo: {
      id: filteredDealers[0]?.id,
      name: filteredDealers.length === 2
        ? `${filteredDealers[0]?.name} & ${filteredDealers[1]?.name}`
        : filteredDealers[0]?.name,
      brand: filteredDealers[0]?.brand,
    },
    adAlert: adAlert || {
      make: selectBrandValue ?? '',
      adPromo: false,
      adCarLaunches: false,
    },
  };

  const filterRoadshowsInDateRange = (items: CamelCaseRoadshowItem[]) => {
    const now = new Date();

    return items.filter((item) => {
      const start = new Date(item.startDate);
      const end = new Date(item.endDate);

      return now >= start && now <= end;
    });
  };

  const eventsList = filterRoadshowsInDateRange(roadShowList);

  const [isBrandMenuOpen, setIsCarMakeMenuOpen] = useState(false);
  const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);

  const handleBrandMenuOpenChange = (isOpen: boolean) => {
    setIsCarMakeMenuOpen(isOpen);
  };

  const handleStatusMenuOpenChange = (isOpen: boolean) => {
    setIsStatusMenuOpen(isOpen);
  };

  useEffect(() => {
    if (!allPromotion) return;

    const pid = searchParams.get('pid');

    if (typeof window !== 'undefined'
      && window.innerWidth < 992
      && pid !== null) {
      const promoCarInfo = allPromotion.find(
        promotion => promotion.id === Number(pid),
      );

      if (promoCarInfo === undefined) return;

      router.push(`${joinSgcmMsSite(`new-cars/info/${promoCarInfo?.carId}/${promoCarInfo?.slug}/promo#${promoCarInfo?.id}`)}`);
    }

    if (isDesktopMode) {
      const openModalFromHash = () => {
        if (pid) {
          const promoId = Number(pid);

          if (!Number.isNaN(promoId)) {
            const found = allPromotion.find(promotion => promotion.id === promoId); // @suppress longLineCheck

            if (found) {
              setActivePromotionListPromoId(promoId);
              setIsPromotionDetailsModalOpen(true);
            }
          }
        }
      };

      openModalFromHash();
      window.addEventListener('hashchange', openModalFromHash);
    }
  }, [isDesktopMode, isMobileMode, allPromotion, searchParams, router]);

  const onSlotRenderEndedRef = useRef<OnSlotRenderEnded>(({
    isEmpty,
    slot,
  }) => {
    if (!isEmpty) {
      return;
    }

    window.googletag.cmd.push(() => {
      window.googletag.pubads().refresh([
        slot,
      ]);
    });
  });

  return (
    <main
      className={styles.containerPageRoot}>
      <div className={styles.pageContainerRoot}>
        <div className={styles.containerBreadcrumb}>
          <Breadcrumb
            list={breadcrumbList} />
        </div>
        <div
          className={styles.containerSearchFilter}>
          <SearchFilterSection
            brandList={brandList}
            defaultSearchData={{ query: {} }}
            isFilterModalOpen={appContextData?.isFilterModalOpen ?? false}
            simpleMode
            onFilterClick={handleFilterClick} />
        </div>
        <h1 className={styles.textTitle}>
          {PAGE_TITLE_ALL_NEW_PROMOTIONS_LIST}
        </h1>
        <div className={styles.containerNewCarPromoContent}>
          <div className={styles.containerLeft}>
            <div className={styles.containerSelect}>
              <div className={styles.containerSelectBrandAndStatus}>
                <div className={styles.selectBrand}>
                  <Select
                    headerClassName={cx(
                      styles.headerPaddingClassName,
                      styles.isBrandWidth,
                      isBrandMenuOpen && styles.isCarMakeMenuOpen,
                      selectBrandValue !== '' && styles.isActive,
                    )}
                    menuBorderRadius={MENU_BORDER_RADIUS_EXTRA_LARGE}
                    menuContainerClassName={cx(
                      styles.containerSelectMenu,
                      styles.isBrandWidth,
                    )}
                    menuLayerPosition={MENU_LAYER_POSITION_FIXED}
                    optionList={
                      brandList.map(brand => ({
                        value: brand,
                        text: brand,
                      }))
                    }
                    placeholder={{
                      text: 'All Brands',
                    }}
                    value={selectBrandValue}
                    onChange={handleSelectBrandOnClick}
                    onMenuOpenChange={handleBrandMenuOpenChange} />
                </div>
                <div className={styles.selectStatus}>
                  <Select
                    headerClassName={cx(
                      styles.headerPaddingClassName,
                      styles.isStatusWidth,
                      isStatusMenuOpen && styles.isCarMakeMenuOpen,
                      selectStatusValue !== '' && styles.isActive,
                    )}
                    menuBorderRadius={MENU_BORDER_RADIUS_EXTRA_LARGE}
                    menuContainerClassName={cx(
                      styles.containerSelectMenu,
                      styles.isStatusWidth,
                    )}
                    menuLayerPosition={MENU_LAYER_POSITION_FIXED}
                    optionList={
                      ALL_STATUS.map(status => ({
                        value: status.value,
                        text: status.text,
                      }))
                    }
                    placeholder={{
                      text: 'Status',
                    }}
                    value={selectStatusValue}
                    onChange={handleSelectStatusOnClick}
                    onMenuOpenChange={handleStatusMenuOpenChange} />
                </div>
              </div>
              <div className={styles.linkDealerLogin}>
                <Link href={`${joinSgcmSite('/dealer_ad/login.php')}`}>Dealer Login</Link>
              </div>
            </div>
            { selectBrandValue && (
              <div className={styles.containerAlertMe}>
                <div className={styles.textCarBrand}>
                  Alert me to all
                  {' '}
                  {selectBrandValue}
                  {' '}
                  Promotions
                </div>
                <button
                  className={styles.btnAddToAlert}
                  onClick={handleAlertClick}>
                  Add to Alert
                </button>
              </div>
            )}
            <div className={styles.containerPricelitsTypeTabs}>
              <Pill
                activeBackgroundColor={PILL_ACTIVE_BACKGROUND_COLOR_RED_1}
                activeTextColor={PILL_ACTIVE_BACKGROUND_COLOR_RED_1}
                activeTextWeight={PILL_ACTIVE_TEXT_WEIGHT_SEMIBOLD}
                backgroundColor={PILL_BACKGROUND_COLOR_GRAY_1}
                className={cx(
                  styles.pill,
                  styles.containerPricelistTypePill,
                  styles.pillPadding,
                )}
                hoverTextColor={PILL_ACTIVE_BACKGROUND_COLOR_RED_1}
                isActive={selectedTab === AUTHORISED_DISTRIBUTORS}
                textColor={PILL_BACKGROUND_COLOR_GRAY_1}
                onClick={handlePriceListTypeChange(AUTHORISED_DISTRIBUTORS)}>
                <span className={styles.textPillTitle}>
                  Authorised Distributors
                </span>
              </Pill>
              <Pill
                activeBackgroundColor={PILL_ACTIVE_BACKGROUND_COLOR_RED_1}
                activeTextColor={PILL_ACTIVE_BACKGROUND_COLOR_RED_1}
                activeTextWeight={PILL_ACTIVE_TEXT_WEIGHT_SEMIBOLD}
                backgroundColor={PILL_BACKGROUND_COLOR_GRAY_1}
                className={cx(
                  styles.pill,
                  styles.containerPricelistTypePill,
                )}
                hoverTextColor={PILL_ACTIVE_BACKGROUND_COLOR_RED_1}
                isActive={selectedTab === PARALLEL_IMPORTERS}
                textColor={PILL_BACKGROUND_COLOR_GRAY_1}
                onClick={handlePriceListTypeChange(PARALLEL_IMPORTERS)}>
                <span className={styles.textPillTitle}>
                  Parallel Importers
                </span>
              </Pill>
              <Pill
                activeBackgroundColor={PILL_ACTIVE_BACKGROUND_COLOR_RED_1}
                activeTextColor={PILL_ACTIVE_BACKGROUND_COLOR_RED_1}
                activeTextWeight={PILL_ACTIVE_TEXT_WEIGHT_SEMIBOLD}
                backgroundColor={PILL_BACKGROUND_COLOR_GRAY_1}
                className={cx(
                  styles.pill,
                  styles.containerPricelistTypePill,
                )}
                hoverTextColor={PILL_ACTIVE_BACKGROUND_COLOR_RED_1}
                isActive={selectedTab === ELECTRIC_VEHICLES}
                textColor={PILL_BACKGROUND_COLOR_GRAY_1}
                onClick={handlePriceListTypeChange(ELECTRIC_VEHICLES)}>
                <span className={styles.textPillTitle}>
                  Electric Vehicles
                </span>
              </Pill>
              <Pill
                activeBackgroundColor={PILL_ACTIVE_BACKGROUND_COLOR_RED_1}
                activeTextColor={PILL_ACTIVE_BACKGROUND_COLOR_RED_1}
                activeTextWeight={PILL_ACTIVE_TEXT_WEIGHT_SEMIBOLD}
                backgroundColor={PILL_BACKGROUND_COLOR_GRAY_1}
                className={cx(
                  styles.pill,
                  styles.containerPricelistTypePill,
                )}
                hoverTextColor={PILL_ACTIVE_BACKGROUND_COLOR_RED_1}
                isActive={selectedTab === ALL_NEW_PROMOTIONS}
                textColor={PILL_BACKGROUND_COLOR_GRAY_1}
                onClick={handlePriceListTypeChange(ALL_NEW_PROMOTIONS)}>
                <span className={styles.textPillTitle}>
                  All Promotions
                </span>
              </Pill>
            </div>
            {promotionList && promotionList.length === 0
              ? (
                <div className={styles.containerNoSearchResult}>
                  <div>
                    <div className={styles.containerImage}>
                      <Image
                        alt={NO_SEARCH_RESULT_ALT}
                        height={NO_SEARCH_RESULT_IMAGE_HEIGHT}
                        src={`${joinSgcmMedia(NO_SEARCH_RESULT_IMAGE)}`}
                        width={NO_SEARCH_RESULT_IMAGE_WIDTH} />
                    </div>
                    <div className={styles.textNoResult}>
                      {searchParams.get('brand')
                        ? `No ${searchParams.get('brand')} Promotions Available`
                        : 'No Promotions Available'}
                    </div>
                  </div>
                </div>
              )
              : (
                <div className={styles.textResult}>
                  {totalNumberOfPromo}
                  {' '}
                  {getSelectedTabLabel(selectedTab)}
                  {' '}
                  Promos
                </div>
              )}
            <div>
              { promotionList?.map((item, index) => (
                <div key={item.id}>
                  <PromoItem
                    isExpired={isExpired(item)}
                    item={item}
                    onItemClick={(promoId) => {
                      setActivePromotionListPromoId(promoId);

                      if (isDesktopMode) {
                        setIsPromotionDetailsModalOpen(true);
                      } else {
                        router.push(`${joinSgcmMsSite(`new-cars/info/${item.carId}/${item.slug}/promo#${item.id}`)}`);
                      }
                    }} />
                  {index === 8 && !isDesktopMode && (
                    <div className={styles.containerShowcaseInList}>
                      {' '}
                      <NewCarShowcase eventItem={
                        eventsList ? eventsList[0] : null
                      } />
                      {' '}
                    </div>
                  )}
                  {index === 2 && !isDesktopMode
                  && promotionList.length > 2 && (
                    <div
                      key="google-ads-banner1"
                      className={styles.containerAds}>
                      <GoogleAds
                        adUnitPath={promoListingBanner1.adUnitPath}
                        divId={promoListingBanner1.divId}
                        slot={promoListingBanner1.slot}
                        targetingMap={{
                          ...promoListingBanner1.targetingMap,
                        }}
                        onSlotRenderEndedRef={onSlotRenderEndedRef} />
                    </div>
                  )}
                  {index === 7 && !isDesktopMode
                  && promotionList.length > 7 && (
                    <div
                      key="google-ads-banner2"
                      className={styles.containerAds}>
                      <GoogleAds
                        adUnitPath={promoListingBanner2.adUnitPath}
                        divId={promoListingBanner2.divId}
                        slot={promoListingBanner2.slot}
                        targetingMap={{
                          ...promoListingBanner2.targetingMap,
                        }}
                        onSlotRenderEndedRef={onSlotRenderEndedRef} />
                    </div>
                  )}
                </div>
              )) }
              <div className={styles.containerNewCarShowcase}>
                {promotionList
                && promotionList.length < 9
                && !isDesktopMode && (
                  <div className={styles.containerLastShowcaseInList}>
                    <NewCarShowcase eventItem={
                      eventsList ? eventsList[0] : null
                    } />
                  </div>
                )}
              </div>
            </div>
            { totalNumberOfPromo !== null && totalNumberOfPromo >= 15 && (
              <div className={styles.containerPagination}>
                <div className={styles.pagination}>
                  {currentPage && lastPage && (
                    <Pagination
                      totalPage={lastPage}
                      value={currentPage}
                      onPageChange={handlePageChange} />
                  )}
                </div>
              </div>
            ) }
          </div>
          <div className={styles.containerRight}>
            <NewCarShowcase eventItem={eventsList ? eventsList[0] : null} />
            <PromoByCarMake allPromotion={allPromotion} />
          </div>
        </div>
        <PromotionDetailsModal
          key={promotionDetailsModalKey}
          isOpen={isPromotionDetailsModalOpen}
          promotion={activePromotionDetailsItem}
          setOpen={setIsPromotionDetailsModalOpen}
          onClose={() => {
            setIsPromotionDetailsModalOpen(false);
          }}
          onExit={() => {
            setPromotionDetailsModalKey(createTimestampUid());
          }} />
        <AlertModal
          adAlertModalContent={adAlertModalContent}
          isOpen={isAlertModalOpen}
          type="adAlert"
          user={user}
          onClose={() => {
            setIsAlertModalOpen(false);
          }}
          onSubmit={handleAlertModalSubmit} />
      </div>
    </main>
  );
};
